import React from 'react';
import { ArticleHero } from '../components/ui/ArticleHero';
import { TableOfContents } from '../components/ui/TableOfContents';
import { ContentSection } from '../components/ui/ContentSection';
import { CheckList } from '../components/ui/CheckList';
import { ComparisonCard } from '../components/ui/ComparisonCard';
import { KeyTakeaways } from '../components/ui/KeyTakeaways';
import { FAQAccordion } from '../components/ui/FAQAccordion';
import { Footer } from '../components/ui/Footer';
import { AlertTriangle, Info, ShieldCheck, X } from 'lucide-react';
export function TaxesArticle() {
  return <div className="min-h-screen bg-slate-50 font-sans flex flex-col" data-id="element-205">
      <ArticleHero data-id="element-206" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10 pb-20 flex-grow w-full" data-id="element-207">
        <div className="flex gap-12" data-id="element-208">
          {/* Main Content */}
          <main className="flex-1 bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-slate-100" data-id="element-209">
            {/* Introduction */}
            <div className="prose prose-lg max-w-none text-slate-700 mb-12" data-id="element-210">
              <p className="lead text-xl md:text-2xl font-medium text-slate-900 mb-6" data-id="element-211">
                Hit a big win on Sweet Bonanza? Triggered the Mega Moolah wheel?
                Before you celebrate, you're probably wondering:{' '}
                <span className="italic text-purple-700" data-id="element-212">
                  "Do I have to pay taxes on this?"
                </span>
              </p>
              <p data-id="element-213">
                It's one of the most common questions Canadian slot players ask,
                and the answer might surprise you. In this complete guide, we'll
                break down everything you need to know about taxes on slot
                winnings in Canada, what the CRA says, and how to protect
                yourself.
              </p>
            </div>

            {/* Quick Answer */}
            <ContentSection id="quick-answer" title="The Quick Answer: Are Slot Winnings Taxable?" data-id="element-214">
              <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg mb-8" data-id="element-215">
                <div className="flex items-start gap-4" data-id="element-216">
                  <ShieldCheck className="w-8 h-8 text-green-600 flex-shrink-0" data-id="element-217" />
                  <div data-id="element-218">
                    <h3 className="text-xl font-bold text-green-900 mb-2" data-id="element-219">
                      For recreational players: NO.
                    </h3>
                    <p className="text-green-800" data-id="element-220">
                      Your slot winnings are <strong data-id="element-221">NOT taxable</strong> in
                      Canada. If you're playing slots for entertainment and fun
                      (which describes 99% of players), you don't pay taxes on
                      your winnings. This applies whether you win $50 or
                      $50,000.
                    </p>
                  </div>
                </div>
              </div>
              <p data-id="element-222">
                However, there's an important exception regarding "professional"
                gamblers which we'll cover below.
              </p>
            </ContentSection>

            {/* Rec vs Pro */}
            <ContentSection id="rec-vs-pro" title="Recreational vs Professional Gambler" data-id="element-223">
              <p className="mb-6" data-id="element-224">
                The Canada Revenue Agency (CRA) distinguishes between two types
                of gamblers. It's crucial to understand where you fit.
              </p>

              <div className="grid md:grid-cols-2 gap-8" data-id="element-225">
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200" data-id="element-226">
                  <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-227">
                    <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm" data-id="element-228">
                      1
                    </span>
                    Recreational (You)
                  </h3>
                  <CheckList type="success" items={['Play for entertainment and fun', 'Not your primary source of income', 'Play occasionally or regularly, but casually', 'No systematic approach to profit']} data-id="element-229" />
                  <div className="mt-4 pt-4 border-t border-slate-200" data-id="element-230">
                    <p className="font-bold text-green-600" data-id="element-231">
                      ✅ Winnings are tax-free
                    </p>
                    <p className="text-sm text-slate-500" data-id="element-232">
                      Losses are NOT deductible
                    </p>
                  </div>
                </div>

                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200" data-id="element-233">
                  <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-234">
                    <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm" data-id="element-235">
                      2
                    </span>
                    Professional (Rare)
                  </h3>
                  <CheckList type="neutral" items={['Gambling is your primary occupation', 'Systematic approach to generating profit', 'Detailed records and strategies', 'Gambling income is your livelihood']} data-id="element-236" />
                  <div className="mt-4 pt-4 border-t border-slate-200" data-id="element-237">
                    <p className="font-bold text-red-600" data-id="element-238">
                      ❌ Winnings are taxable business income
                    </p>
                    <p className="text-sm text-slate-500" data-id="element-239">
                      Losses ARE deductible
                    </p>
                  </div>
                </div>
              </div>
            </ContentSection>

            {/* CRA Factors */}
            <ContentSection id="cra-factors" title="How Does CRA Determine If You're Professional?" data-id="element-240">
              <p className="mb-6" data-id="element-241">
                The CRA uses several factors to classify gamblers. Unless you
                check ALL these boxes, you are likely recreational.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8" data-id="element-242">
                <div className="p-4 rounded-lg bg-white border border-slate-200 shadow-sm" data-id="element-243">
                  <h4 className="font-bold text-slate-900 mb-2" data-id="element-244">
                    Frequency & Duration
                  </h4>
                  <p className="text-sm text-slate-600" data-id="element-245">
                    How often do you gamble? Is it a full-time job's worth of
                    hours?
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-white border border-slate-200 shadow-sm" data-id="element-246">
                  <h4 className="font-bold text-slate-900 mb-2" data-id="element-247">
                    Primary Income
                  </h4>
                  <p className="text-sm text-slate-600" data-id="element-248">
                    Can you support yourself without gambling? Do you have
                    another job?
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-white border border-slate-200 shadow-sm" data-id="element-249">
                  <h4 className="font-bold text-slate-900 mb-2" data-id="element-250">
                    Systematic Approach
                  </h4>
                  <p className="text-sm text-slate-600" data-id="element-251">
                    Do you use specific strategies, math, and detailed records
                    to ensure profit?
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-white border border-slate-200 shadow-sm" data-id="element-252">
                  <h4 className="font-bold text-slate-900 mb-2" data-id="element-253">Intent</h4>
                  <p className="text-sm text-slate-600" data-id="element-254">
                    Are you gambling strictly for profit like a business, or for
                    the thrill?
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg flex items-start gap-3 text-blue-800 text-sm" data-id="element-255">
                <Info className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-256" />
                <p data-id="element-257">
                  <strong data-id="element-258">Reality Check:</strong> Unless you're literally making
                  a living from slots with documented systems and no other
                  income, you're recreational. The CRA doesn't come after casual
                  players.
                </p>
              </div>
            </ContentSection>

            {/* Jackpots */}
            <ContentSection id="jackpots" title="What About Big Jackpot Wins?" data-id="element-259">
              <p className="mb-4" data-id="element-260">
                Won the Mega Moolah wheel for $2 million? Good news:
              </p>
              <CheckList items={["Still tax-free if you're a recreational player", 'You keep 100% of your winnings', 'No withholding by the casino', 'No reporting to CRA required']} data-id="element-261" />

              <h3 className="text-xl font-bold text-slate-900 mt-8 mb-4" data-id="element-262">
                What The Casino Might Ask For
              </h3>
              <p className="mb-4" data-id="element-263">
                For large wins (typically $10,000+), casinos may request ID and
                complete a Large Cash Transaction Report.{' '}
                <strong data-id="element-264">Important:</strong> This is for anti-money laundering
                (FINTRAC) compliance, NOT for tax purposes.
              </p>
            </ContentSection>

            {/* Online */}
            <ContentSection id="online" title="Online Casino Winnings" data-id="element-265">
              <p className="mb-6" data-id="element-266">
                Whether you play on Canadian-based sites (like OLG or PlayNow)
                or international offshore casinos, the rules remain the same.
              </p>

              <div className="bg-slate-900 text-white p-6 rounded-xl mb-6" data-id="element-267">
                <h4 className="font-bold text-lg mb-4 text-yellow-400" data-id="element-268">
                  Banking Considerations for Offshore Wins
                </h4>
                <ul className="space-y-3 text-slate-300" data-id="element-269">
                  <li className="flex gap-3" data-id="element-270">
                    <span className="text-yellow-500" data-id="element-271">•</span>
                    Large international transfers can trigger bank questions
                  </li>
                  <li className="flex gap-3" data-id="element-272">
                    <span className="text-yellow-500" data-id="element-273">•</span>
                    Banks must report transactions over $10,000 (FINTRAC rules)
                  </li>
                  <li className="flex gap-3" data-id="element-274">
                    <span className="text-yellow-500" data-id="element-275">•</span>
                    This is anti-money laundering, not taxation
                  </li>
                </ul>
              </div>
            </ContentSection>

            {/* Comparison */}
            <ContentSection id="us-comparison" title="Canadian vs American Players" data-id="element-276">
              <p data-id="element-277">
                Canadians have a massive advantage over their southern neighbors
                when it comes to gambling taxes.
              </p>
              <ComparisonCard data-id="element-278" />
            </ContentSection>

            {/* Records */}
            <ContentSection id="records" title="Do I Need To Keep Records?" data-id="element-279">
              <div className="space-y-6" data-id="element-280">
                <div data-id="element-281">
                  <h3 className="font-bold text-lg text-slate-900 mb-2" data-id="element-282">
                    For Recreational Players
                  </h3>
                  <p className="text-slate-600 mb-3" data-id="element-283">
                    Technically no, but it's smart to:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-slate-600" data-id="element-284">
                    <li data-id="element-285">Track big wins and losses (rough records)</li>
                    <li data-id="element-286">Keep casino statements for large wins</li>
                    <li data-id="element-287">Save withdrawal confirmations</li>
                  </ul>
                  <p className="text-sm text-slate-500 mt-2 italic" data-id="element-288">
                    Why? Peace of mind and in case CRA ever questions your
                    assets.
                  </p>
                </div>

                <div className="border-t border-slate-200 pt-6" data-id="element-289">
                  <h3 className="font-bold text-lg text-slate-900 mb-2" data-id="element-290">
                    For Professional Gamblers
                  </h3>
                  <p className="text-slate-600 mb-3" data-id="element-291">
                    Absolutely yes. You need detailed win/loss records,
                    receipts, time logs, and strategy documentation.
                  </p>
                </div>
              </div>
            </ContentSection>

            {/* Myths */}
            <ContentSection id="myths" title="Common Myths Debunked" data-id="element-292">
              <div className="grid gap-4" data-id="element-293">
                {[{
                myth: 'The casino reports big wins to CRA',
                fact: 'False. Only suspicious transactions are reported for money laundering.'
              }, {
                myth: "If I win too much, I'll owe taxes",
                fact: 'False. $100 or $100,000 = both tax-free for recreational players.'
              }, {
                myth: 'I can deduct my losses',
                fact: 'False. Recreational players cannot deduct losses.'
              }, {
                myth: 'Online wins are different from casino wins',
                fact: 'False. Same tax treatment applies.'
              }].map((item, i) => <div key={i} className="flex gap-4 p-4 bg-red-50/50 rounded-lg border border-red-100" data-id="element-294">
                    <X className="w-6 h-6 text-red-500 flex-shrink-0" data-id="element-295" />
                    <div data-id="element-296">
                      <p className="font-bold text-red-900 mb-1" data-id="element-297">
                        Myth: "{item.myth}"
                      </p>
                      <p className="text-slate-700" data-id="element-298">{item.fact}</p>
                    </div>
                  </div>)}
              </div>
            </ContentSection>

            {/* Key Takeaways */}
            <KeyTakeaways points={['Recreational slot winnings are 100% tax-free in Canada', 'No reporting to CRA required for casual players', 'You keep all your winnings, unlike US players', 'Losses are not deductible for recreational players', 'Online and in-person wins are treated the same', 'Professional gamblers (rare) DO pay taxes']} data-id="element-299" />

            {/* FAQ */}
            <ContentSection id="faq" title="" data-id="element-300">
              <FAQAccordion items={[{
              question: 'Do I need to report slot winnings on my tax return?',
              answer: "No, not if you're a recreational player. There is no line on the Canadian tax return for lottery or gambling winnings for non-professionals."
            }, {
              question: 'What if I win $1 million on a progressive jackpot?',
              answer: 'Still tax-free for recreational players. You keep it all. The amount does not change the tax status.'
            }, {
              question: 'Can I deduct my slot losses?',
              answer: 'No, only professional gamblers who report winnings as business income can deduct losses.'
            }, {
              question: 'Will the casino give me a tax form?',
              answer: 'No, Canadian casinos do not issue tax forms (like the W-2G in the US) for winnings.'
            }, {
              question: 'What about online casino winnings from Malta or Curacao sites?',
              answer: 'The same rules apply. As long as you are a recreational player in Canada, the winnings are tax-free regardless of where the casino is licensed.'
            }]} data-id="element-301" />
            </ContentSection>

            {/* Disclaimer */}
            <div className="mt-12 p-6 bg-slate-100 rounded-xl text-sm text-slate-500 border border-slate-200" data-id="element-302">
              <div className="flex items-center gap-2 mb-2 font-bold text-slate-700" data-id="element-303">
                <AlertTriangle className="w-4 h-4" data-id="element-304" />
                <span data-id="element-305">Disclaimer</span>
              </div>
              <p data-id="element-306">
                This guide provides general information based on current CRA
                guidelines as of 2026. Tax laws can change, and individual
                situations vary. For personalized tax advice, consult a licensed
                Canadian tax professional or accountant.
              </p>
            </div>
          </main>

          {/* Sidebar (Table of Contents) */}
          <TableOfContents data-id="element-307" />
        </div>
      </div>

      <Footer data-id="element-308" />
    </div>;
}